import { LanguageCodeSchema } from "@/entities/translation/language-code.schema"
import { z } from "zod"
import {
  QuotaStatusEnum,
  TranslationEngineEnum,
  TranslationModeEnum,
  TranslationPhaseEnum,
  TranslationStatusEnum,
} from "./translation.types"

export const GlossarySchema = z.object({
  id: z.string(),
  name: z.string(),
  ownerId: z.string(),
  mappings: z.record(z.string(), z.string()),
  sourceLanguage: LanguageCodeSchema,
  targetLanguage: LanguageCodeSchema,
})

export const GlossarySnakeCaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  owner_id: z.string(),
  mappings: z.record(z.string(), z.string()),
  source_language: LanguageCodeSchema,
  target_language: LanguageCodeSchema,
})

export const CreateGlossaryReqSchema = z.object({
  glossaryName: z.string(),
  sourceLanguage: LanguageCodeSchema,
  targetLanguage: LanguageCodeSchema,
})

export const MachineTranslationFormSchema = z.object({
  domain: z.string().min(1).max(64).optional(),
  inputPath: z.string().optional(),
  sourceLanguage: LanguageCodeSchema,
  targetLanguage: LanguageCodeSchema,
  sourceContent: z.string().optional(),
  engine: z.enum(TranslationEngineEnum).default(TranslationEngineEnum.BeringAI),
})

export const FileQueueSchema = z.object({
  id: z.string(),
  sourceLanguage: LanguageCodeSchema,
  targetLanguage: LanguageCodeSchema,
  domain: z.string().min(1).max(64),
  status: z.enum(TranslationStatusEnum),
  sourceTitle: z.string(),
  errorCode: z.number().nullable(),
  jobId: z.string(),
  inputPath: z.string(),
  isDeleted: z.boolean(),
  smartGlossaryExtracted: z.boolean(),
  // An omitted engine is the legacy/default BeringAI contract, not a partial
  // response. Non-default engines are always explicit.
  engine: z.enum(TranslationEngineEnum).optional(),
  // Optional only for mixed-version rollout compatibility with older SSE
  // producers. Current producers include all three lineage fields.
  version: z.number().optional(),
  lineageId: z.string().optional(),
  parentJobId: z.string().nullable().optional(),
  // Optional while task_management versions without progress snapshots may
  // still serve the list SSE during a rolling deployment.
  processedSegments: z.number().optional(),
  totalSegments: z.number().optional(),
  translationPhase: z.enum(TranslationPhaseEnum).nullable().optional(),
})

export const MtTextJobSchema = z.object({
  id: z.string(),
  sourceLanguage: LanguageCodeSchema,
  targetLanguage: LanguageCodeSchema,
  domain: z.string().min(1).max(64),
  status: z.enum(TranslationStatusEnum),
  unitCount: z.string(),
  createDt: z.string(),
})

export const AlternativeSchema = z.object({
  alternative_text: z.string(),
  segment_id: z.string(),
})

export const TextTranslationProgressSchema = z.object({
  jobId: z.string(),
  isFinal: z.boolean(),
  status: z.enum(TranslationStatusEnum),
  data: z.object({
    segments: z.array(
      z.object({
        alternatives: z.array(AlternativeSchema),
        target_segment: z.string(),
        id: z.string(),
      })
    ),
    totalCount: z.number(),
  }),
})

export const TranslationSegmentSchema = z.object({
  text: z.string(),
  id: z.string(),
})

export const MtDocumentJobSchema = FileQueueSchema.extend({
  createDt: z.string(),
  unitCount: z.string(),
})

export const MtFileSchema = z.object({
  localId: z.number(),
  object: z.instanceof(File),
  path: z.string(),
  fileName: z.string().optional(),
  fileExt: z.string().optional(),
  inputPath: z.string().optional(),
  error: z.boolean().optional(),
  errorMessage: z.string().optional(),
  pageCount: z.number().optional(),
  // Individual settings for each file
  sourceLanguage: LanguageCodeSchema.optional(),
  targetLanguage: LanguageCodeSchema.optional(),
  domain: z.string().min(1).max(64).optional(),
  isLlmPeEnabled: z.boolean().optional(),
  glossaryId: z.string().nullable().optional(),
  parentJobId: z.string().optional(),
})

export const presignedUrlSchema = z.object({
  inputPath: z.string(),
  url: z.string(),
})

export const FileUrlSchema = z.object({ url: z.string() })

export const FeedbackSchema = z.object({
  liked: z.boolean().optional(),
  reportingUrl: z.string(),
  content: z.string().nullable(),
  reportedAt: z.string(),
  userId: z.string(),
  sourceContent: z.string().optional(),
  translatedContent: z.string().optional(),
  sourceLanguage: z.string().optional(),
  targetLanguage: z.string().optional(),
  translationJobId: z.string().optional(),
})

export const TranslationSearchSchema = z.object({
  mode: z
    .nativeEnum(TranslationModeEnum)
    .optional()
    .default(TranslationModeEnum.Text),
})

// Per-term voting record from mt-worker. Optional because mt-worker may
// not have emitted a voting record (rare defensive fallback per spec).
export const VotingLogEntrySchema = z.object({
  src: z.string(),
  candidates: z.record(z.string(), z.number()),
  selected: z.string(),
  isCustomGlossary: z.boolean(),
  batch: z.number(),
})

export const SmartGlossarySchema = z.object({
  jobId: z.string(),
  sourceLanguage: LanguageCodeSchema,
  targetLanguage: LanguageCodeSchema,
  glossary: z.array(
    z.object({
      id: z.string(),
      sourceTerm: z.string(),
      targetTerm: z.string(),
      isCustomGlossary: z.boolean(),
      votingLog: VotingLogEntrySchema.nullable(),
    })
  ),
})

// PUT /smart-glossaries/{id} body — single-row update with the new
// per-term endpoint.
export const UpdateSmartGlossaryReqSchema = z.object({
  targetTerm: z.string(),
})

export const PredictCostRequestSchema = z.object({
  sourceLanguage: LanguageCodeSchema,
  targetLanguage: LanguageCodeSchema,
  sourceContent: z.string(),
  domain: z.string().min(1).max(64),
  kind: z.string(),
})

export const PredictCostResponseSchema = z.object({
  status: z.enum(QuotaStatusEnum),
  unitCount: z.string(),
  currentUsage: z.string(),
  quota: z.string(),
  remainingQuota: z.string(),
  usagePercentage: z.string(),
  willExceed: z.boolean(),
  overageUnits: z.string().optional(),
  costInfo: z.object({
    overageRate: z.string().optional(),
    overageCost: z.string(),
    currency: z.string(),
  }),
})

// Media Translation Schemas
export const SubtitleSchema = z.object({
  startTime: z.number(), // seconds
  endTime: z.number(),
  original: z.string(),
  translated: z.string(),
})

export const TranscriptItemSchema = z.object({
  id: z.string(),
  original: z.string(),
  translated: z.string(),
})

export const MediaTranslationResultSchema = z.object({
  jobId: z.string(),
  type: z.enum(["video", "audio", "transcript"]),
  videoUrl: z.string(),
  originalVideoUrl: z.string(),
  subtitles: z.array(SubtitleSchema),
  transcript: z.array(TranscriptItemSchema),
  sourceLanguage: LanguageCodeSchema,
  targetLanguage: LanguageCodeSchema,
})
